import React, { useState, useRef } from "react";
import VideoPlayer from "../components/Video/VideoPlayer";
import VideoContent, {
  VideoContentRef,
} from "../components/Video/VideoContent";
import {
  UserVideoWord,
  UserWordStatus,
  VideoActivity,
  Lesson,
} from "@shared_types";
import { VideoPlayerHandle } from "../components/Video/VideoPlayer";
import { callWithAuth, putWordStatus } from "../api/Api";
import { PutWordStatusRequest } from "@shared_types";
import "../App.css";
import { getPlatform } from "@/lib/iap";
import { useDivSize } from "@/hooks/UseDivSize";
import { LessonHeaderHeightPx } from "@/components/Activities/LessonHeader";
import ContinueButton from "@/components/Buttons/ContinueButton";
import { isInvalidDictionaryWord } from "@/components/Subtitles/invalidDictionaryWord";

interface VideoPageProps {
  chapterId: number;
  lesson: Lesson;
  videoActivity: VideoActivity;
  onActivityFinished: () => void;
  onUpdateUserWords: (ds: UserVideoWord[]) => void;
  onTrackDropsAdded: (drops: number) => void;
  onTrackLearningAdded: (learningWords: number) => void;
  onTrackKnownAdded: (knownWords: number) => void;
  onTrackListeningTime: (time: number) => void;
  isHorizontal: boolean;
}

const VideoPage: React.FC<VideoPageProps> = ({
  lesson,
  videoActivity,
  onActivityFinished,
  onUpdateUserWords,
  onTrackDropsAdded,
  onTrackKnownAdded,
  onTrackLearningAdded,
  onTrackListeningTime,
  isHorizontal,
}) => {
  const [selectedWord, setSelectedWord] = useState({} as UserVideoWord);
  const targetWord = selectedWord?.displayText || "";
  const nativeWord = selectedWord?.definition?.nativeWord || "";
  const videoRef = useRef<VideoPlayerHandle>(null);
  const videoContentRef = useRef<VideoContentRef>(null);
  const [isDisplayHelp, setIsDisplayHelp] = useState(true);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const startTime = videoActivity.start;
  const endTime = videoActivity.end;
  const userVideoWords = videoActivity.userVideoWords;
  const platform = getPlatform();
  // prevents the flickering from updating currentTime
  // once wordClick is started
  const pauseHandleTimeUpdate = useRef(false);
  const { ref: videoPageRef, height: videoPageHeight } = useDivSize();
  const { ref: videoContentDivRef, height: videoContentDivHeight } =
    useDivSize();

  let bottomPadding = 15;
  if (isHorizontal) {
    bottomPadding = 15;
  }

  let videoContentStyleHeight = "var(--video-content-height)";

  if (isHorizontal) {
    videoContentStyleHeight = "calc(var(--video-content-height) + 20px)";
  }

  const videoPlayerHeight = `${
    videoPageHeight -
    videoContentDivHeight -
    LessonHeaderHeightPx -
    bottomPadding +
    22
  }px`;

  const handleWordClick = async (word: UserVideoWord) => {
    pauseHandleTimeUpdate.current = true;
    setSelectedWord(word);
    setCurrentTime(word.start);
    setIsDisplayHelp(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.seekTo(word.start);
    }
    if (
      word.status == "known" ||
      word.status == "deleted" ||
      word.status == "learning"
    ) {
      return;
    }
    if (isInvalidDictionaryWord(word.displayText)) {
      return;
    }
    const learningDropsAmount = 3;
    if (word.status == "new") {
      onTrackDropsAdded(learningDropsAmount);
    }
    updateUserWordStatus(word, "learning").catch((e) => {
      console.error(e);
      // revert the granted drops if it failed on learning
      if (word.status == "learning") {
        onTrackDropsAdded(-learningDropsAmount);
      }
    });
    updateUIUserVideoWords(word, "learning");
  };

  const handleKnownClick = async (word: UserVideoWord) => {
    if (isInvalidDictionaryWord(word.displayText)) {
      return;
    }
    let newStatus: UserWordStatus = "known";
    if (word.status == "known") {
      newStatus = "learning";
      onTrackKnownAdded(-1);
    }
    if (newStatus == "known") {
      onTrackKnownAdded(1);
    }
    if (newStatus == "learning") {
      onTrackLearningAdded(1);
    }
    updateUserWordStatus(word, newStatus).catch((e) => {
      console.error(e);
      // revert the granted drops if it failed on learning
      if (newStatus == "learning") {
        onTrackLearningAdded(-1);
      }
      if (newStatus == "known") {
        onTrackKnownAdded(-1);
      }
    });
    updateUIUserVideoWords(word, newStatus);
    const newWord: UserVideoWord = {
      ...word,
      status: newStatus,
    };
    setSelectedWord(newWord);
  };

  const updateUserWordStatus = async (
    word: UserVideoWord,
    newStatus: UserWordStatus
  ) => {
    let attempts = 0;
    const maxAttempts = 7;
    while (attempts < maxAttempts) {
      try {
        const data: PutWordStatusRequest = {
          wordKey: word.wordKey,
          newStatus: newStatus,
          definitionId: word.definition.definitionId,
          lessonId: lesson.lessonId,
        };
        await callWithAuth(putWordStatus, data);
        return; // Success, exit the function
      } catch (e) {
        attempts++;
        console.error(
          `Error updating word status (attempt ${attempts}/${maxAttempts}):`,
          e
        );
        // If we've reached max attempts, revert UI and throw error
        if (attempts >= maxAttempts) {
          // we dont want to ever distrupt the user expereince.
          // so if requests fail after so many retries, just log the error
          // the UI can be refreshed when they revist but I dont want them knowinng
          // that things went really wrong.
          console.error("Max retry attempts reached.");
        }
        // Wait before retrying (exponential backoff)
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * Math.pow(2, attempts - 1))
        );
      }
    }
  };

  const updateUIUserVideoWords = (
    word: UserVideoWord,
    newStatus: UserWordStatus
  ) => {
    const newUserVideoWords = newUserVideoWordsWithUpdatesStatus(
      word,
      newStatus
    );
    onUpdateUserWords(newUserVideoWords);
  };

  // updates the stats of the specific word
  const newUserVideoWordsWithUpdatesStatus = (
    word: UserVideoWord,
    newStatus: UserWordStatus
  ): UserVideoWord[] => {
    const newVws: UserVideoWord[] = [];
    for (const uvw of userVideoWords) {
      let newWord: UserVideoWord;
      if (uvw.wordKey == word.wordKey) {
        newWord = { ...uvw, status: newStatus };
      } else {
        newWord = uvw;
      }
      newVws.push(newWord);
    }
    return newVws;
  };

  const handleTimeUpdate = (timeMs: number) => {
    if (!pauseHandleTimeUpdate.current) {
      setCurrentTime(timeMs);
    }
  };

  const handlePlay = () => {
    pauseHandleTimeUpdate.current = false;
    videoContentRef.current?.clearPhraseSelection();
  };

  // Handle phrase selection start - pause the video
  const handlePhraseSelectStart = () => {
    pauseHandleTimeUpdate.current = true;
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <div
      ref={videoPageRef}
      className={`relative flex flex-col items-center w-full max-w-[700px] h-[100svh]`}
    >
      <VideoPlayer
        ref={videoRef}
        youtubeId={videoActivity.youtubeId}
        onTimeUpdate={handleTimeUpdate}
        startTime={startTime as number}
        endTime={endTime as number}
        onPlay={handlePlay}
        onTrackListeningTime={onTrackListeningTime}
        className={"w-full h-full"}
        isHorizontal={isHorizontal}
        videoPlayerHeight={videoPlayerHeight}
      />
      <div
        ref={videoContentDivRef}
        style={{ minHeight: videoContentStyleHeight, height: "auto" }}
        className={`absolute bottom-10 flex flex-col justify-end flex-grow w-full bg-[#fafafa] max-h-[320px] h-[30%] gap-1 rounded-t-xl`}
      >
        <VideoContent
          ref={videoContentRef}
          lessonId={lesson.lessonId}
          currentTime={currentTime as number}
          userVideoWords={userVideoWords}
          onWordClick={handleWordClick}
          onKnownClick={handleKnownClick}
          onPhraseSelectStart={handlePhraseSelectStart}
          clickHelp={selectedWord.clickHint}
          selectedVideoWordId={selectedWord.videoWordId}
          isDisplayHelp={isDisplayHelp}
          targetWord={targetWord}
          nativeWord={nativeWord}
          selectedWordStatus={selectedWord.status}
          isHorizontal={isHorizontal}
        />
        <div
          className={`flex justify-center items-center px-4 ${
            platform === "web" ? "bottom-padding-web" : "pb-6"
          }`}
          style={{ pointerEvents: "auto" }}
        >
          <ContinueButton
            onClick={() => {
              onActivityFinished();
            }}
            text={"Let's practice!"}
          />
        </div>
      </div>
    </div>
  );
};

export default VideoPage;
